import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertBanner, Button, Card, Select, Skeleton, StatusBadge, Textarea, ToggleSwitch, useLocale, useToast } from '@ministry/ui/internal';

import { getMyAvailability, updateMyAvailability, AVAILABILITY_STATUSES, availabilityPublicLabel } from '../lib/availability.js';
import { availabilityStatusEntry } from '../lib/appointment-lifecycle.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { useT } from '../i18n/strings.js';
import { PageHeader } from '../components/PageHeader.js';

/**
 * SCREEN_SPECS.md I12. Manual availability, Phase 1 (A-003 — no biometric feed yet).
 *
 * AC-19's coarseness is deliberate and visible here, not just enforced server-side: the
 * form shows what the CITIZEN will actually see next to what the officer is about to set,
 * so nobody mistakes the internal note (never published) for public copy, and nobody
 * expects a "back at 3pm" precision the public projection does not carry at all (no
 * location, no fine timestamp in the schema — this screen never renders a control for
 * either).
 */
export function MyAvailabilityPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [statusCode, setStatusCode] = React.useState<string | undefined>(undefined);
  const [note, setNote] = React.useState('');
  const [quickAvailable, setQuickAvailable] = React.useState(false);

  const query = useQuery({
    queryKey: ['my-availability'],
    queryFn: ({ signal }) => getMyAvailability(signal),
  });

  React.useEffect(() => {
    if (query.data) {
      setStatusCode(query.data.status.code);
      setNote(query.data.internalNote ?? '');
      setQuickAvailable(query.data.status.code === 'available');
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (input: { statusCode: string; internalNote?: string | null }) =>
      updateMyAvailability({ ...input, expectedVersion: query.data?.version ?? 0 }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['my-availability'], updated);
      setStatusCode(updated.status.code);
      setNote(updated.internalNote ?? '');
      setQuickAvailable(updated.status.code === 'available');
      // Named defect: "My Availability shows saved and never removed" — a bare
      // `mutation.isSuccess` span has no way to go back to `false` on its own (nothing
      // ever resets it, so it sits there until the next mutation attempt). A toast is
      // the one mechanism this app now has for "confirm success visibly, then get out
      // of the way" (§6 #22: success auto-dismisses; it never needs a manual clear).
      push({ variant: 'success', title: t('availability.my.saved') });
    },
  });

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton shape="block" className="h-8 w-64" />
        <Skeleton shape="list-row" count={3} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('availability.my.title')} />
        <AlertBanner variant="critical" title={t('availability.my.loadError.title')} action={<Button size="sm" variant="secondary" onClick={() => query.refetch()}>{t('common.retry')}</Button>}>
          {t('availability.my.loadError.body')}
        </AlertBanner>
      </div>
    );
  }

  const current = query.data;
  if (!current) return <></>;

  /**
   * SCREEN_SPECS.md I12's exact wording: "Your status couldn't be updated. It still shows
   * [previous status] publicly." — the stale-but-wrong PUBLIC status is the concrete harm
   * (a wasted citizen trip), so the banner always names what the public currently sees,
   * reading `details.current` when the server sent the fresher value (a genuine
   * `CONFLICT_HOLDER_CHANGED`, AC-21) and otherwise the last value this screen itself
   * loaded — either way, never silent about what is actually live right now.
   */
  const submitError = mutation.error;
  let errorBanner: React.ReactNode = null;
  if (submitError instanceof ApiError || submitError instanceof NetworkError) {
    const stillCurrentCode =
      submitError instanceof ApiError && submitError.code === 'CONFLICT_HOLDER_CHANGED'
        ? ((submitError.details?.current as { status?: { code: string } } | undefined)?.status?.code ?? current.status.code)
        : current.status.code;
    const publicLabel = availabilityPublicLabel(stillCurrentCode, locale);
    errorBanner = (
      <AlertBanner variant="critical" title={t('availability.my.saveError.title')}>
        {t('availability.my.saveError.body', { status: publicLabel })}
      </AlertBanner>
    );
  }

  const selectedPreviewCode = statusCode ?? current.status.code;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('availability.my.title')} description={t('availability.my.description')} />

      <Card className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-text-primary">{t('availability.my.current')}</span>
          <StatusBadge entry={availabilityStatusEntry(current.status.code, current.status.labelNe, current.status.labelEn)} />
        </div>

        {errorBanner}

        <ToggleSwitch
          id="availability-quick-toggle"
          label={t('availability.my.quickToggle')}
          checked={quickAvailable}
          loading={mutation.isPending && mutation.variables?.statusCode === 'available'}
          onCheckedChange={(checked) => {
            setQuickAvailable(checked);
            const next = checked ? 'available' : 'not_published';
            setStatusCode(next);
            mutation.mutate({ statusCode: next, internalNote: note || null });
          }}
        />

        <Select
          id="availability-status-select"
          label={t('availability.my.statusLabel')}
          options={AVAILABILITY_STATUSES.map((s) => ({ value: s.code, label: locale === 'ne' ? s.labelNe : s.labelEn }))}
          value={statusCode}
          onValueChange={(value) => {
            setStatusCode(value);
            setQuickAvailable(value === 'available');
          }}
          required
        />

        <p className="rounded-md bg-surface-muted p-3 text-sm text-text-muted">
          <span className="font-semibold text-text-primary">{t('availability.my.citizenSees')}</span>{' '}
          {availabilityPublicLabel(selectedPreviewCode, locale)}
        </p>

        <Textarea
          id="availability-internal-note"
          label={t('availability.my.noteLabel')}
          helperText={t('availability.my.noteHelper')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
        />

        <div>
          <Button
            onClick={() => statusCode && mutation.mutate({ statusCode, internalNote: note || null })}
            loading={mutation.isPending}
            loadingLabel={t('availability.my.saving')}
            disabled={!statusCode}
          >
            {t('availability.my.save')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
